import {AxiosResponse} from 'axios'
import {HttpInstance} from '../createHttpInstance'
import {decodeBox, decodeVerdict, isStringList} from './getFaceOffRun'
import {decodeRun, isFiniteNumber, isRecord} from './getFaceOffRuns'
import {
  FaceOffGoldenCase,
  FaceOffGoldenEdit,
  FaceOffGoldenPhoto,
  FaceOffGoldenSummary,
  FaceOffGoldenVersion,
} from './types'

export const FACE_OFF_GOLDEN_URL = '/v2/faces/faceoff/golden'
export const FACE_OFF_GOLDEN_EDITS_URL = '/v2/faces/faceoff/golden/edits'

/** Lists the golden versions: GET /v2/faces/faceoff/golden. */
export const getFaceOffGoldenVersions = (
  http: HttpInstance,
): Promise<AxiosResponse<unknown>> =>
  http.request<unknown>({method: 'GET', url: FACE_OFF_GOLDEN_URL})

/** One golden version: GET /v2/faces/faceoff/golden/{version}. */
export const getFaceOffGoldenVersion = (
  http: HttpInstance,
  version: number,
): Promise<AxiosResponse<unknown>> =>
  http.request<unknown>({
    method: 'GET',
    url: `${FACE_OFF_GOLDEN_URL}/${version}`,
  })

/** Removes a photo or a case, which writes a new version. */
export const postFaceOffGoldenEdit = (
  http: HttpInstance,
  edit: FaceOffGoldenEdit,
): Promise<AxiosResponse<unknown>> =>
  http.request<unknown>({
    method: 'POST',
    url: FACE_OFF_GOLDEN_EDITS_URL,
    data: edit,
  })

const fail = (what: string): never => {
  throw new Error(`Malformed Face Off golden response: ${what}`)
}

/** Decodes one version summary, also media's answer to an edit. */
export const decodeGoldenSummary = (
  raw: unknown,
  what = 'summary',
): FaceOffGoldenSummary => {
  if (
    !isRecord(raw) ||
    !isFiniteNumber(raw.version) ||
    typeof raw.createdUtc !== 'string' ||
    !isFiniteNumber(raw.photos) ||
    !isFiniteNumber(raw.cases)
  ) {
    return fail(what)
  }
  const optional = <T>(
    value: unknown,
    ok: (v: unknown) => v is T,
  ): T | null => {
    if (value === undefined || value === null) return null
    return ok(value) ? value : fail(what)
  }
  const isString = (v: unknown): v is string => typeof v === 'string'
  const signOffs = raw.signOffs ?? []
  if (
    !Array.isArray(signOffs) ||
    !signOffs.every(
      (s) =>
        isRecord(s) && typeof s.by === 'string' && typeof s.at === 'string',
    )
  ) {
    return fail(what)
  }
  // extends, source, note, signOffs and lastPassRate are newer; an older
  // media leaves them out.
  return {
    version: raw.version,
    createdUtc: raw.createdUtc,
    photos: raw.photos,
    cases: raw.cases,
    extends: optional(raw.extends, isFiniteNumber),
    source: optional(raw.source, isString) || null,
    note: optional(raw.note, isString) || null,
    signOffs: signOffs.map((s) => ({by: s.by, at: s.at})),
    lastPassRate: optional(raw.lastPassRate, isFiniteNumber),
  }
}

/** Decodes the version list. One bad entry rejects the whole response. */
export const decodeGoldenVersions = (body: unknown): FaceOffGoldenSummary[] => {
  if (!isRecord(body) || !Array.isArray(body.versions)) return fail('versions')
  return body.versions.map((raw, i) =>
    decodeGoldenSummary(raw, `versions[${i}]`),
  )
}

const isOptionalNumber = (value: unknown): value is number | null | undefined =>
  value === undefined || value === null || isFiniteNumber(value)

const decodeCase = (raw: unknown, what: string): FaceOffGoldenCase => {
  if (
    !isRecord(raw) ||
    !isFiniteNumber(raw.personId) ||
    typeof raw.truth !== 'string' ||
    typeof raw.kind !== 'string' ||
    !(raw.baselineScore === null || isFiniteNumber(raw.baselineScore)) ||
    !isFiniteNumber(raw.refs) ||
    !isOptionalNumber(raw.score) ||
    !isOptionalNumber(raw.threshold) ||
    (raw.refUrls !== undefined && !isStringList(raw.refUrls))
  ) {
    return fail(what)
  }
  // bbox, score, threshold, refUrls and verdict are newer; an older media
  // leaves them out and the review shows no box, score or training photo.
  return {
    personId: raw.personId,
    truth: raw.truth,
    kind: raw.kind,
    baselineScore: raw.baselineScore,
    refs: raw.refs,
    bbox: raw.bbox === undefined ? null : decodeBox(raw.bbox, what),
    score: raw.score ?? null,
    threshold: raw.threshold ?? null,
    refUrls: raw.refUrls ?? [],
    verdict:
      raw.verdict === undefined ? null : decodeVerdict(raw.verdict, what),
  }
}

const decodePhoto = (raw: unknown, index: number): FaceOffGoldenPhoto => {
  const what = `photos[${index}]`
  if (
    !isRecord(raw) ||
    !isFiniteNumber(raw.photoId) ||
    !isFiniteNumber(raw.clientId) ||
    !isFiniteNumber(raw.albumId) ||
    typeof raw.reason !== 'string' ||
    typeof raw.url !== 'string' ||
    !Array.isArray(raw.cases)
  ) {
    return fail(what)
  }
  return {
    photoId: raw.photoId,
    clientId: raw.clientId,
    albumId: raw.albumId,
    reason: raw.reason,
    url: raw.url,
    cases: raw.cases.map((c, i) => decodeCase(c, `${what}.cases[${i}]`)),
  }
}

/** Decodes one golden version. One bad field rejects the whole response. */
export const decodeGoldenVersion = (body: unknown): FaceOffGoldenVersion => {
  if (
    !isRecord(body) ||
    !isFiniteNumber(body.version) ||
    !Array.isArray(body.photos) ||
    !Array.isArray(body.untags)
  ) {
    return fail('version')
  }
  return {
    version: body.version,
    summary:
      body.summary === undefined || body.summary === null
        ? null
        : decodeGoldenSummary(body.summary),
    photos: body.photos.map(decodePhoto),
    untags: body.untags.map((raw, i) => {
      if (
        !isRecord(raw) ||
        !isFiniteNumber(raw.photoId) ||
        !isFiniteNumber(raw.personId)
      ) {
        return fail(`untags[${i}]`)
      }
      return {photoId: raw.photoId, personId: raw.personId}
    }),
    latestRun:
      body.latestRun === undefined || body.latestRun === null
        ? null
        : decodeRun(body.latestRun, 0),
  }
}
